import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  TextField,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  styled
} from '@mui/material';
import {
  CloudUpload,
  SearchOutlined,
  BuildOutlined,
  AssignmentOutlined,
  PhotoCamera,
  UploadFile,
  BarChart,
  CheckCircle,
  PlaylistAddCheck,
  LocalOffer,
  ArrowForward,
  Store,
  GetApp,
  LocationOn,
  ShoppingCart,
  Delete,
  Engineering,
  ReportProblem
} from '@mui/icons-material';
import DiagnosticsImage from '../assets/Diagnostics1.png'; // Ensure the path is correct

// Styled components
const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #FFE302',
  borderRadius: 0,
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 227, 2, 0.05)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(255, 227, 2, 0.1)',
    transform: 'translateY(-4px)'
  }
}));

const DiagnosticButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#00A651',
  color: '#fff',
  padding: '12px 30px',
  fontWeight: 700,
  borderRadius: 0,
  '&:hover': {
    backgroundColor: '#008C44',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 166, 81, 0.2)'
  }
}));

const ResultCard = styled(Card)(({ theme, variant }) => ({
  borderRadius: 0,
  height: '100%',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  border: variant === 'critical' ? '1px solid #f44336' :
          variant === 'warning' ? '1px solid #ff9800' :
          variant === 'good' ? '1px solid #4caf50' : 'none',
  '& .MuiCardContent-root': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  }
}));

const Diagnostics = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [confidenceScore, setConfidenceScore] = useState(0);

  const steps = [
    'Upload Images & Documents',
    'AI Analysis',
    'View Recommendations',
    'Summary & Next Steps'
  ];

  const handleUpload = (type) => {
    // Simulate file upload
    const newFile = {
      id: Date.now(),
      name: type === 'image' ? 'device_photo.jpg' : 'error_log.pdf',
      type: type,
      size: '2.4 MB'
    };
    setUploadedFiles([...uploadedFiles, newFile]);
  };

  const removeFile = (id) => {
    setUploadedFiles(uploadedFiles.filter(file => file.id !== id));
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // Simulate AI analysis
      setIsAnalyzing(true);
      const timer = setInterval(() => {
        setConfidenceScore(oldProgress => {
          const newProgress = Math.min(oldProgress + Math.random() * 10, 100);
          if (newProgress === 100) {
            clearInterval(timer);
            setTimeout(() => {
              setIsAnalyzing(false);
              setAnalysisComplete(true);
              setActiveStep(prevActiveStep => prevActiveStep + 1);
            }, 500);
          }
          return newProgress;
        });
      }, 500);
    } else {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
    if (activeStep === 2) {
      setAnalysisComplete(false);
      setConfidenceScore(0);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              Upload clear photos and relevant documents of your device
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <UploadBox onClick={() => handleUpload('image')}>
                  <PhotoCamera sx={{ fontSize: 60, color: '#FFE302', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Upload Images
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 2 }}>
                    Take clear photos of the faulty device or specific components
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    sx={{
                      bgcolor: '#000',
                      color: '#FFE302',
                      '&:hover': { bgcolor: '#333' }
                    }}
                  >
                    Select Images
                  </Button>
                </UploadBox>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <UploadBox onClick={() => handleUpload('document')}>
                  <UploadFile sx={{ fontSize: 60, color: '#FFE302', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Upload Documents
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 2 }}>
                    Share diagnostic reports, error logs, or warranty papers
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    sx={{
                      bgcolor: '#000',
                      color: '#FFE302',
                      '&:hover': { bgcolor: '#333' }
                    }}
                  >
                    Select Documents
                  </Button>
                </UploadBox>
              </Grid>
            </Grid>
            
            {uploadedFiles.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Uploaded Files
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f8f8f8' }}>
                  <List>
                    {uploadedFiles.map(file => (
                      <ListItem
                        key={file.id}
                        secondaryAction={
                          <IconButton edge="end" onClick={() => removeFile(file.id)}>
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          {file.type === 'image' ? <PhotoCamera /> : <UploadFile />}
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${file.type}, ${file.size}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>
              AI-Powered Analysis in Progress
            </Typography>
            
            <Box sx={{ bgcolor: '#f8f8f8', p: 4, textAlign: 'center', mb: 4 }}>
              <SearchOutlined sx={{ fontSize: 80, color: '#00A651', mb: 2 }} />
              
              <Typography variant="h6" sx={{ mb: 3 }}>
                {isAnalyzing ? 'Analyzing your device...' : 'Ready to analyze'}
              </Typography>
              
              {isAnalyzing && (
                <Box sx={{ width: '100%', mb: 3 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={confidenceScore} 
                    sx={{ 
                      height: 10,
                      borderRadius: 5,
                      bgcolor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#00A651',
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    {Math.round(confidenceScore)}% Complete
                  </Typography>
                </Box>
              )}
              
              <Grid container spacing={3} sx={{ textAlign: 'left', mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BarChart sx={{ color: '#00A651', mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Real-Time Fault Detection
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Our AI is scanning images and documents to identify possible hardware issues
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PlaylistAddCheck sx={{ color: '#00A651', mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Database Comparison
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Cross-checking with our database of known hardware problems and solutions
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ color: '#00A651', mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Confidence Scoring
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Calculating confidence level for detected faults based on multiple factors
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        );
        
      case 2:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>
              Diagnostic Results & Recommendations
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f8f8' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      AI Diagnostic Summary
                    </Typography>
                    <Chip 
                      label="85% Confidence" 
                      sx={{ 
                        bgcolor: '#00A651', 
                        color: 'white',
                        fontWeight: 600
                      }} 
                    />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Based on our analysis, we've detected the following issues with your device:
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ReportProblem sx={{ color: '#f44336' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Screen damage detected" 
                        secondary="The display shows crack patterns consistent with impact damage"
                      />
                      <Chip label="Critical" sx={{ bgcolor: '#f44336', color: 'white' }} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ReportProblem sx={{ color: '#ff9800' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Battery performance issues" 
                        secondary="Battery health is approximately 65%, below optimal performance"
                      />
                      <Chip label="Warning" sx={{ bgcolor: '#ff9800', color: 'white' }} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4caf50' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Motherboard functioning normally" 
                        secondary="No issues detected with the main board components"
                      />
                      <Chip label="Good" sx={{ bgcolor: '#4caf50', color: 'white' }} />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                  Suggested Fixes & Recommendations
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <ResultCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <BuildOutlined sx={{ fontSize: 40, color: '#00A651', mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        DIY Repair Options
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                      For the battery issue, you can try these solutions:
                    </Typography>
                    
                    <List dense sx={{ mb: 'auto' }}>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText primary="Perform a full system reset" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText primary="Update device firmware/software" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText primary="Calibrate battery (instructions provided)" />
                      </ListItem>
                    </List>
                    
                    <Button 
                      variant="outlined"
                      fullWidth
                      sx={{ 
                        mt: 3,
                        borderColor: '#00A651',
                        color: '#00A651',
                        '&:hover': { bgcolor: 'rgba(0,166,81,0.05)' }
                      }}
                    >
                      View DIY Guide
                    </Button>
                  </CardContent>
                </ResultCard>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <ResultCard variant="critical">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <LocalOffer sx={{ fontSize: 40, color: '#00A651', mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Replacement Parts
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                      We recommend these genuine replacement parts:
                    </Typography>
                    
                    <List dense sx={{ mb: 'auto' }}>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Original Screen Replacement" 
                          secondary="$120.00" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Genuine Battery Replacement" 
                          secondary="$45.00"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Installation Tools Kit" 
                          secondary="$15.00"
                        />
                      </ListItem>
                    </List>
                    
                    <Button 
                      variant="contained"
                      fullWidth
                      sx={{ 
                        mt: 3,
                        bgcolor: '#000',
                        color: '#FFE302',
                        '&:hover': { bgcolor: '#333' }
                      }}
                    >
                      View Parts
                    </Button>
                  </CardContent>
                </ResultCard>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <ResultCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Engineering sx={{ fontSize: 40, color: '#00A651', mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Expert Consultation
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                      For screen replacement, we recommend professional help:
                    </Typography>
                    
                    <List dense sx={{ mb: 'auto' }}>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText primary="Book a video consultation with our experts" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText primary="Schedule an in-person repair appointment" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowForward sx={{ fontSize: 16, color: '#00A651' }} />
                        </ListItemIcon>
                        <ListItemText primary="Get a professional cost estimate" />
                      </ListItem>
                    </List>
                    
                    <DiagnosticButton fullWidth sx={{ mt: 3 }}>
                      Connect with Expert
                    </DiagnosticButton>
                  </CardContent>
                </ResultCard>
              </Grid>
            </Grid>
          </Box>
        );
        
      case 3:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>
              Diagnostic Report & Next Steps
            </Typography>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f8f8' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Complete Diagnostic Report
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<GetApp />}
                      sx={{
                        bgcolor: '#000',
                        color: '#FFE302',
                        '&:hover': { bgcolor: '#333' }
                      }}
                    >
                      Download PDF
                    </Button>
                  </Box>
                  
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Device Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666' }}>Device Type:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>Smartphone</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666' }}>Model:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>XYZ Pro 12</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666' }}>OS Version:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>Version 14.2</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666' }}>Age:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>18 months</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Detected Issues Summary
                    </Typography>
                    <List>
                      <ListItem sx={{ bgcolor: 'rgba(244,67,54,0.1)', mb: 1 }}>
                        <ListItemText 
                          primary="Screen Damage (Critical)" 
                          secondary="Impact damage detected on front glass panel and LCD"
                        />
                      </ListItem>
                      <ListItem sx={{ bgcolor: 'rgba(255,152,0,0.1)', mb: 1 }}>
                        <ListItemText 
                          primary="Battery Health (Warning)" 
                          secondary="Battery capacity at 65%, rapid discharge observed"
                        />
                      </ListItem>
                      <ListItem sx={{ bgcolor: 'rgba(76,175,80,0.1)' }}>
                        <ListItemText 
                          primary="System Performance (Good)" 
                          secondary="Processing speeds and memory usage within normal range"
                        />
                      </ListItem>
                    </List>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Cost Estimates
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Screen Replacement:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>3,500 DZD</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Battery Replacement:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>1,200 DZD</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Professional Installation:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>800 DZD</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Estimated Total:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>5,500 DZD</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                  Recommended Next Steps
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Store sx={{ mr: 1 }} /> Nearby Service Centers
                  </Typography>
                  
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Tech Repair Express</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>123 Main Street, Algiers</Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>⭐⭐⭐⭐⭐ (4.8/5) • 0.8 km away</Typography>
                      <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ borderColor: '#00A651', color: '#00A651' }}
                      >
                        Get Directions
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Mobile Repair Center</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>45 Technology Avenue, Algiers</Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>⭐⭐⭐⭐ (4.5/5) • 2.3 km away</Typography>
                      <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ borderColor: '#00A651', color: '#00A651' }}
                      >
                        Get Directions
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
                
                <DiagnosticButton 
                  fullWidth 
                  variant="contained"
                  startIcon={<ShoppingCart />}
                  sx={{ mb: 3 }}
                >
                  Purchase Replacement Parts
                </DiagnosticButton>
                
                <Button 
                  fullWidth 
                  variant="outlined"
                  sx={{
                    borderColor: '#000',
                    color: '#000',
                    padding: '12px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#000',
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  Schedule a Repair Appointment
                </Button>
              </Grid>
            </Grid>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh', pb: 8 }}>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#000', 
        color: '#fff',
        py: 6
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 900, 
                  mb: 2,
                  color: '#FFE302'
                }}
              >
                AI-Powered Device Diagnostics
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontWeight: 400,
                  opacity: 0.9,
                  maxWidth: '800px'
                }}
              >
                Upload images of your device and let our advanced AI identify issues, suggest fixes, and connect you with professional repair services.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  icon={<CheckCircle sx={{ color: '#000 !important' }} />} 
                  label="Fast Analysis" 
                  sx={{ 
                    bgcolor: '#FFE302', 
                    color: '#000',
                    fontWeight: 600,
                    mr: 1
                  }}
                />
                <Chip 
                  icon={<CheckCircle sx={{ color: '#000 !important' }} />} 
                  label="High Accuracy" 
                  sx={{ 
                    bgcolor: '#FFE302', 
                    color: '#000',
                    fontWeight: 600,
                    mr: 1
                  }}
                />
                <Chip 
                  icon={<CheckCircle sx={{ color: '#000 !important' }} />} 
                  label="Expert Solutions" 
                  sx={{ 
                    bgcolor: '#FFE302', 
                    color: '#000',
                    fontWeight: 600
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box 
                component="img"
                src={DiagnosticsImage}
                alt="AI Diagnostics"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                  objectFit: 'contain'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 5 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{
                  sx: {
                    color: activeStep >= index ? '#00A651' : undefined,
                  }
                }}
              >
                <Typography sx={{ fontWeight: activeStep >= index ? 700 : 400 }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Step Content */}
        <Box>
          {renderStepContent(activeStep)}
        </Box>
        
        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ 
                mr: 2, 
                borderColor: '#000',
                color: '#000',
                '&:hover': {
                  borderColor: '#000',
                  bgcolor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ 
                bgcolor: activeStep === 1 && isAnalyzing ? '#ccc' : '#00A651',
                '&:hover': {
                  bgcolor: activeStep === 1 && isAnalyzing ? '#ccc' : '#008C44'
                },
                color: '#fff',
                fontWeight: 600
              }}
              disabled={activeStep === 1 && isAnalyzing}
            >
              {activeStep === 0 ? 'Start Analysis' : 
               activeStep === 1 ? (isAnalyzing ? 'Analyzing...' : 'View Results') : 
               'Continue'}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Diagnostics; 